/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/no-explicit-any */
import createStore from 'zustand';
import shallow from 'zustand/shallow';
import useProductsIM from 'hooks/useProductsIM';
import useInventoryEntryIM from 'hooks/useMovementsIM';
import { ImProduct } from 'interfaces/imProduct.interface';
import { useState } from 'react';
import useCustomSnackbar from 'hooks/useCustomSnackbar';
import { SALIDA } from 'constants/constBd';
import { HeaderData, MovementProducts, ProductOutputManagement } from 'interfaces/productOutputManagement.interface';

const defaultHeaderData = {
  remission: '',
  invoice: '',
  purchaseOrder: '',
  observations: '',
  documentTypeId: SALIDA
};

export const productsOutputManagementStore = createStore<ProductOutputManagement>((set, get) => ({
  searchText: '',
  setSearchText: (searchText: string) => set({ searchText }),
  products: [],
  setProducts: (products: ImProduct[]) => set({ products }),
  headerData: defaultHeaderData,
  setHeaderData: (headerData: HeaderData) => set({ headerData }),
  cartItemCount: 0,
  incrementCartItemCount: () => {
    const count = get().cartItemCount + 1;
    set({ cartItemCount: count });
    return count;
  },
  cartItems: [],
  setCartItems: (cartItems) => set({ cartItems }),
  isLoadingSave: false,
  setIsLoadingSave: (isLoadingSave) => set({ isLoadingSave }),
  reset: () => set({ searchText: '', products: [], headerData: defaultHeaderData, cartItemCount: 0, cartItems: [] })
}));

const useProductsOutputManagementStore = () => {
  const [
    products,
    setProducts,
    cartItems,
    setCartItems,
    setIsLoadingSave,
    cartItemCount,
    incrementCartItemCount,
    headerData,
    setHeaderData,
    setSearchText
  ] = productsOutputManagementStore(
    (state) => [
      state.products,
      state.setProducts,
      state.cartItems,
      state.setCartItems,
      state.setIsLoadingSave,
      state.cartItemCount,
      state.incrementCartItemCount,
      state.headerData,
      state.setHeaderData,
      state.setSearchText
    ],
    shallow
  );

  const { getProducts: getProductService } = useProductsIM();
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const { showNotistack } = useCustomSnackbar();
  const { createMovement } = useInventoryEntryIM();

  const saveMovement = (): void => {
    setIsLoadingSave(true);

    const data: MovementProducts[] = [];
    try {
      if (!headerData.purchaseOrder) {
        throw Error('Número de orden es obligatorio');
      }

      if (cartItems.length === 0) {
        throw Error('Debes agregar al menos un producto');
      }

      cartItems.forEach((item) => {
        if (!item.quantity) {
          throw Error('Producto a ingresar sin costo unitario o cantidad');
        }

        data.push({
          parentId: item.parentId,
          productId: item.productId,
          discountsWp: item.discountsWp,
          quantity: item.quantity
        });
      });

      const dataToSend = [
        {
          document: headerData,
          products: data
        }
      ];

      createMovement(dataToSend)
        .then((response: boolean) => {
          if (response === true) {
            setCartItems([]);
            setProducts([]);
            setSearchText('');
            setHeaderData(defaultHeaderData);
            showNotistack({ message: 'La salida se guardó correctamente' });
          } else {
            showNotistack({ message: 'Ocurrió un error' });
          }
        })
        .catch((err: any) => {
          console.log(err.reponse?.data);
          showNotistack({ message: 'Ocurrió un error' });
        })
        .finally(() => {
          setIsLoadingSave(false);
        });
    } catch (error) {
      showNotistack({ message: (error as any).message });
      setIsLoadingSave(false);
    }
  };

  return {
    products,
    getProducts: (page: number, limit: number, searchText: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        setIsLoadingProducts(true);

        getProductService(page, limit, searchText)
          .then((result: any) => {
            const produts: ImProduct[] = [];

            result?.products.map((product: ImProduct) => {
              if (product.type === 'variable') {
                product.variation?.map((variation: any) => {
                  produts.push({
                    name: variation.name,
                    parentId: Number(variation.parentId),
                    productId: Number(variation.productId),
                    quantity: Number(variation.stock_quantity),
                    sku: variation.sku,
                    stock: Number(variation.stock),
                    type: 'variación',
                    unitCost: product.unitCost
                  } as ImProduct);
                });
              } else {
                produts.push(product);
              }
            });

            setProducts(produts);
            resolve();
          })
          .catch((err) => reject(err))
          .finally(() => setIsLoadingProducts(false));
      });
    },
    isLoadingProducts,
    resetProducts: () => setProducts([]),
    cartItems,
    saveMovement,
    setCartItems,
    cartItemCount,
    incrementCartItemCount
  };
};

export default useProductsOutputManagementStore;
